// Withdraw Page - Multiple withdrawal methods
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, CreditCard, RefreshCw, Shield } from "lucide-react";
import { SBER, pageVariants, tapAnimation } from "../utils/constants";
import { SberCard } from "../components/UIComponents";

const WithdrawPage = ({ user, rate, onBack, t }) => {
  const [method, setMethod] = useState(null); // 'crypto', 'card_usd', 'card_kgs'
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState(''); // crypto wallet or card number
  const [loading, setLoading] = useState(false);
  
  const amountNum = parseFloat(amount) || 0;
  const userBalanceUsdt = user?.balance_usd || user?.balance_usdt || 0;
  const userBalanceKgs = user?.balance_kgs || 0;
  
  // Fee calculation based on method
  const getFeeInfo = () => {
    switch(method) {
      case 'crypto':
        return { fee: 1, currency: 'USD', percent: false, min: 10, balance: userBalanceUsdt };
      case 'card_usd':
        return { fee: 2, currency: '%', percent: true, min: 20, balance: userBalanceUsdt };
      case 'card_kgs':
        return { fee: 2.5, currency: '%', percent: true, min: 1000, balance: userBalanceKgs };
      default:
        return { fee: 0, currency: '', percent: false, min: 0, balance: 0 };
    }
  };
  
  const feeInfo = getFeeInfo();
  const fee = feeInfo.percent ? amountNum * (feeInfo.fee / 100) : feeInfo.fee;
  
  // Result calculation
  const getResult = () => {
    if (method === 'crypto' || method === 'card_usd') {
      return { amount: amountNum - fee, currency: 'USD' };
    } else if (method === 'card_kgs') {
      return { amount: amountNum - fee, currency: 'KGS' };
    }
    return { amount: 0, currency: '' };
  };
  
  const result = getResult();
  
  // Validation
  const getAddressLabel = () => {
    switch(method) {
      case 'crypto': return t ? t('withdraw.cardNumber') : 'Адрес TRC-20 кошелька';
      case 'card_usd': 
      case 'card_kgs': return t ? t('withdraw.cardNumber') : 'Номер карты';
      default: return '';
    }
  };
  
  const getAddressPlaceholder = () => {
    switch(method) {
      case 'crypto': return 'T...';
      case 'card_usd':
      case 'card_kgs': return '0000 0000 0000 0000';
      default: return '';
    }
  };
  
  const validateAddress = () => {
    if (!address) return false;
    switch(method) {
      case 'crypto': return address.startsWith('T') && address.length >= 30;
      case 'card_usd':
      case 'card_kgs': return address.replace(/\s/g, '').length >= 16;
      default: return false;
    }
  };
  
  const handleWithdraw = async () => {
    if (amountNum <= 0) {
      toast.error(t ? t('exchange.minAmount') : 'Введите сумму');
      return;
    }
    if (amountNum > feeInfo.balance) {
      toast.error(t ? t('exchange.insufficientFunds') : 'Недостаточно средств');
      return;
    }
    if (!validateAddress()) {
      toast.error(t ? t('errors.somethingWrong') : 'Проверьте адрес получателя');
      return;
    }
    if (user?.kyc_status !== 'APPROVED' && method !== 'crypto') {
      toast.error(t ? t('withdraw.kycRequired') : 'Пройдите верификацию для вывода');
      return;
    }
    
    setLoading(true);
    toast.info('Используйте Telegram бота для вывода');
    setTimeout(() => setLoading(false), 1500);
  };
  
  // Method Selection View
  if (!method) {
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen"
        style={{ background: SBER.bg }}
      >
        <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
          <motion.button 
            whileTap={tapAnimation}
            onClick={onBack}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.grayPale }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
          </motion.button>
          <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
            {t ? t('withdraw.title') : 'Вывод средств'}
          </h1>
        </div>
        
        <div className="px-5 pt-5">
          {/* Balance Card */}
          <SberCard className="p-5 mb-5" accent>
            <div className="text-center">
              <p className="text-white/70 text-[13px] mb-1">{t ? t('withdraw.selectMethod') : 'Доступно для вывода'}</p>
              <p className="text-[32px] font-bold text-white">{userBalanceUsdt.toFixed(2)} USD</p>
              <p className="text-white/60 text-[14px]">{userBalanceKgs.toFixed(0)} KGS</p>
            </div>
          </SberCard>
          
          <p className="text-[15px] font-semibold mb-4" style={{ color: SBER.dark }}>
            {t ? t('withdraw.selectMethod') : 'Выберите способ вывода'}
          </p>
          
          {/* Crypto/USD Account Withdrawal */}
          <SberCard className="p-4 mb-3" onClick={() => setMethod('crypto')}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: SBER.greenPale }}>
                <span className="text-xl font-bold" style={{ color: SBER.green }}>$</span>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[15px]" style={{ color: SBER.dark }}>
                  {t ? t('withdraw.bankTransfer') : 'На валютный счёт'}
                </p>
                <p className="text-[13px]" style={{ color: SBER.grayLight }}>USD • {t ? t('topup.fee') : 'Комиссия'} 1%</p>
              </div>
              <ArrowUpRight className="w-5 h-5" style={{ color: SBER.grayLight }} />
            </div>
          </SberCard>
          
          {/* Card Withdrawal USD */}
          <SberCard className="p-4 mb-3" onClick={() => setMethod('card_usd')}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: SBER.greenPale }}>
                <CreditCard className="w-6 h-6" style={{ color: SBER.green }} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[15px]" style={{ color: SBER.dark }}>
                  {t ? t('withdraw.toCard') : 'На карту'} (USD)
                </p>
                <p className="text-[13px]" style={{ color: SBER.grayLight }}>Visa/MasterCard • {t ? t('topup.fee') : 'Комиссия'} 2%</p>
              </div>
              <ArrowUpRight className="w-5 h-5" style={{ color: SBER.grayLight }} />
            </div>
          </SberCard>
          
          {/* Card Withdrawal KGS */}
          <SberCard className="p-4 mb-3" onClick={() => setMethod('card_kgs')}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: SBER.greenPale }}>
                <CreditCard className="w-6 h-6" style={{ color: SBER.green }} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[15px]" style={{ color: SBER.dark }}>
                  {t ? t('withdraw.toCard') : 'На карту'} (KGS)
                </p>
                <p className="text-[13px]" style={{ color: SBER.grayLight }}>Элкарт/VISA • {t ? t('topup.fee') : 'Комиссия'} 2.5%</p>
              </div>
              <ArrowUpRight className="w-5 h-5" style={{ color: SBER.grayLight }} />
            </div>
          </SberCard>
          
          {/* KYC Warning */}
          {user?.kyc_status !== 'APPROVED' && (
            <div className="mt-5 p-4 rounded-xl" style={{ background: '#FEF3C7' }}>
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 mt-0.5" style={{ color: '#F59E0B' }} />
                <div>
                  <p className="font-medium text-[14px]" style={{ color: '#92400E' }}>
                    {t ? t('kyc.verification') : 'Требуется верификация'}
                  </p>
                  <p className="text-[13px] mt-1" style={{ color: '#B45309' }}>
                    {t ? t('withdraw.kycRequired') : 'Для вывода на карту пройдите KYC-верификацию'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  }
  
  // Withdrawal Form View
  const currencyLabel = method === 'card_kgs' ? 'KGS' : 'USD';
  
  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen"
      style={{ background: SBER.bg }}
    >
      <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
        <motion.button 
          whileTap={tapAnimation}
          onClick={() => setMethod(null)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: SBER.grayPale }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
        </motion.button>
        <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
          {method === 'crypto' ? 'Вывод на кошелёк' : method === 'card_usd' ? 'Вывод на карту (USD)' : 'Вывод на карту (KGS)'}
        </h1>
      </div>
      
      <div className="px-5 pt-5">
        {/* Balance Info */}
        <SberCard className="p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-[13px]" style={{ color: SBER.gray }}>
              {t ? t('exchange.insufficientFunds').split(' ')[0] : 'Доступно'}
            </span>
            <span className="font-semibold text-[15px]" style={{ color: SBER.green }}>
              {feeInfo.balance?.toFixed(method === 'card_kgs' ? 0 : 2)} {currencyLabel}
            </span>
          </div>
        </SberCard>
        
        {/* Amount Input */}
        <SberCard className="p-5 mb-4">
          <label className="text-[13px] font-medium mb-3 block" style={{ color: SBER.green }}>
            {t ? t('withdraw.amount') : 'Сумма'} ({currencyLabel})
          </label>
          <div className="flex items-baseline gap-2">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="text-[36px] font-bold bg-transparent outline-none w-full"
              style={{ color: SBER.dark }}
              data-testid="withdraw-amount-input"
            />
            <span className="text-[20px] font-semibold" style={{ color: SBER.green }}>{currencyLabel}</span>
          </div>
          
          <motion.button
            whileTap={tapAnimation}
            onClick={() => setAmount(String(feeInfo.balance?.toFixed(method === 'card_kgs' ? 0 : 2)))}
            className="mt-3 px-4 py-2 rounded-xl text-[13px] font-semibold"
            style={{ background: SBER.greenPale, color: SBER.green }}
          >
            MAX
          </motion.button>
        </SberCard>
        
        {/* Address Input */}
        <SberCard className="p-5 mb-4">
          <label className="text-[13px] font-medium mb-3 block" style={{ color: SBER.green }}>
            {getAddressLabel()}
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={getAddressPlaceholder()}
            className="w-full text-[16px] bg-transparent outline-none font-medium"
            style={{ color: SBER.dark }}
            data-testid="withdraw-address-input"
          />
        </SberCard>
        
        {/* Calculation */}
        {amountNum > 0 && (
          <SberCard className="p-5 mb-4">
            <div className="space-y-3">
              <div className="flex justify-between text-[14px]">
                <span style={{ color: SBER.gray }}>{t ? t('withdraw.amount') : 'Сумма'}</span>
                <span className="font-medium" style={{ color: SBER.dark }}>{amountNum} {currencyLabel}</span>
              </div>
              <div className="flex justify-between text-[14px]">
                <span style={{ color: SBER.gray }}>
                  {t ? t('topup.fee') : 'Комиссия'} {feeInfo.percent ? `${feeInfo.fee}%` : `${feeInfo.fee} USD`}
                </span>
                <span className="font-medium" style={{ color: '#F59E0B' }}>
                  −{fee.toFixed(2)} {currencyLabel}
                </span>
              </div>
              <div className="h-px" style={{ background: SBER.grayPale }} />
              <div className="flex justify-between items-center">
                <span className="font-semibold" style={{ color: SBER.dark }}>
                  {t ? t('topup.toReceive') : 'Получите'}
                </span>
                <span className="text-[18px] font-bold" style={{ color: SBER.green }}>
                  {result.amount.toFixed(method === 'card_kgs' ? 0 : 2)} {result.currency}
                </span>
              </div>
            </div>
          </SberCard>
        )}
        
        {/* Min amount notice */}
        <p className="text-center text-[13px] mb-4" style={{ color: SBER.grayLight }}>
          {t ? t('topup.min') : 'Минимум'}: {feeInfo.min} {currencyLabel}
        </p>
        
        {/* Withdraw Button */}
        <motion.button
          whileTap={tapAnimation}
          onClick={handleWithdraw}
          disabled={loading || amountNum <= 0 || !validateAddress()}
          className="w-full py-4 rounded-xl font-semibold text-[16px] text-white transition-all
            disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: SBER.green, boxShadow: '0 4px 16px rgba(33, 160, 56, 0.3)' }}
          data-testid="withdraw-button"
        >
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : (t ? t('withdraw.withdrawButton') : 'Вывести')}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default WithdrawPage;
